import { createSlice } from "@reduxjs/toolkit"
import { pnlBudgetType } from "../../pages/PNLBudget/types"
import { TransformData } from "../../components/Table/PNLBudgetTable/utils"
import { pnlValidator } from "../../components/Table/ResourceBudgetTable/validators"
import { toast } from "react-toastify"

const AMOUNT_DECIMALS = 2
const PERCENT_DECIMALS = 1

const getNumericValue = (value: any) => {
  if (typeof value === "number") {
    return value
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = parseFloat(value)
    return Number.isNaN(parsed) ? NaN : parsed
  }
  return NaN
}

const formatAmountValue = (value: number) => {
  if (!Number.isFinite(value)) {
    return 0
  }
  return parseFloat(value.toFixed(AMOUNT_DECIMALS))
}

const formatPercentValue = (value: number) => {
  if (!Number.isFinite(value)) {
    return 0
  }
  return parseFloat(value.toFixed(PERCENT_DECIMALS))
}

const initialState: pnlBudgetType = {
  budgetData: [],
  rigidBudgetData: [],
  budgetYear: null,
  meta: {},
  dirtyCells: [],
  aggregations: [],
  rigidAggregations: [],
  saved: false,
  years: [],
  selectedYear: null,
  resourceBudgetPublished: false,
}

const pnlSlice = createSlice({
  name: "pnl-slice",
  initialState,
  reducers: {
    setBudgetData: (state, action) => {
      state.resourceBudgetPublished =
        action.payload.is_resource_budget_published
      state.budgetYear = parseInt(action.payload?.budget_year)
      const { transformedData, aggregation, headers } = TransformData(
        action.payload,
      )
      state.budgetData = transformedData
      state.aggregations = aggregation
      state.meta = headers
    },
    setRigidBudgetData: (state, action) => {
      const { transformedData, aggregation } = TransformData(action.payload)
      state.rigidBudgetData = transformedData
      state.rigidAggregations = aggregation
    },
    setYears: (state, action) => {
      state.years = action.payload
    },
    setSaved: (state, action) => {
      state.saved = action.payload
    },
    setSelectedYear: (state, action) => {
      state.selectedYear = action.payload
    },
    updateCell: (state, action) => {
      const { index, id, original, updateVal, key } = action.payload

      //? Data Manipulation

      const amountKey = key.split("_")[0] + "_" + "amount"
      const percentKey = key.split("_")[0] + "_" + "percent"
      const saveId = id.replace("percent", "amount")
      const revenueAmount = getNumericValue(state.budgetData[0][amountKey])
      const originalAmountNumeric = getNumericValue(original[amountKey])
      const originalPercentNumeric = getNumericValue(original[percentKey])

      //? Case - when field is cleared

      let updatedRow = { ...original }
      if (!updateVal) {
        if (key.includes("amount")) {
          updatedRow[amountKey] = original[key]
        } else {
          updatedRow[percentKey] = original[key]
        }
        state.budgetData[index] = updatedRow
        return
      }

      const parsedValue = parseFloat(updateVal)
      if (Number.isNaN(parsedValue)) {
        return
      }

      const normalizedInputValue = key.includes("percent")
        ? formatPercentValue(parsedValue)
        : formatAmountValue(parsedValue)

      let newAmount = key.includes("amount")
        ? normalizedInputValue
        : Number.isFinite(originalAmountNumeric)
        ? originalAmountNumeric
        : 0
      let newPercent = key.includes("percent")
        ? normalizedInputValue
        : Number.isFinite(originalPercentNumeric)
        ? originalPercentNumeric
        : 0

      //? Case - when new value is same as before

      const originalNumericValue = getNumericValue(original[key])
      if (
        Number.isFinite(originalNumericValue) &&
        originalNumericValue === normalizedInputValue
      ) {
        return
      }

      //? If Revenue is being set again, give warning and set all values to default again

      if (original?.line_item == "REVENUE") {
        if (state.dirtyCells.find((dirtyCell: any) => dirtyCell == saveId)) {
          updatedRow[amountKey] = original[key]
          state.budgetData[index] = updatedRow
          return
        }
      }

      //? Validation of new value

      const test = pnlValidator({
        field: key,
        value: updateVal,
        digitsLimit: 9,
      })
      if (!test.valid) {
        toast.error(test.message)
        return
      }

      //?

      //? Changing percent according to amount and vice versa
      if (Number.isFinite(revenueAmount) && revenueAmount !== 0) {
        if (key.includes("amount")) {
          newPercent = formatPercentValue(
            (normalizedInputValue / revenueAmount) * 100,
          )
        } else {
          newAmount = formatAmountValue(
            (normalizedInputValue / 100) * revenueAmount,
          )
        }
      }

      updatedRow[amountKey] = newAmount
      updatedRow[percentKey] = newPercent

      //? Update Year Total Column

      const currentTotal = getNumericValue(updatedRow["-total_current"]) || 0
      const safeOriginalAmount = Number.isFinite(originalAmountNumeric)
        ? originalAmountNumeric
        : 0
      updatedRow["-total_current"] =
        currentTotal - safeOriginalAmount + newAmount

      //? Update Footer Values of Year Total Column

      if (original?.line_item !== "REVENUE") {
        state.aggregations[0]["-total_current"] = (
          parseFloat(state.aggregations[0]["-total_current"]) + newAmount
        ).toFixed(AMOUNT_DECIMALS)
        state.aggregations[1]["-total_current"] = (
          parseFloat(state.aggregations[1]["-total_current"]) - newAmount
        ).toFixed(AMOUNT_DECIMALS)
      } else {
        state.aggregations[1]["-total_current"] = (
          parseFloat(state.aggregations[1]["-total_current"]) -
          safeOriginalAmount +
          newAmount
        ).toFixed(AMOUNT_DECIMALS)
      }

      //? Update Year Total Column Percentage for all rows
      // Get revenue total (revenue row's -total_current)
      const revenueRow = state.budgetData.find(
        (row: any) => row.line_item === "REVENUE",
      )
      const revenueTotal = getNumericValue(revenueRow?.["-total_current"]) || 0
      
      // Recalculate percentage for the updated row
      if (revenueTotal > 0) {
        if (original?.line_item === "REVENUE") {
          // Revenue row should always be 100%
          updatedRow["-total_current_percent"] = 100
        } else {
          updatedRow["-total_current_percent"] = formatPercentValue(
            (updatedRow["-total_current"] / revenueTotal) * 100,
          )
        }
      } else {
        updatedRow["-total_current_percent"] = 0
      }

      //? Update Footer Values of Year Total Column Percentage
      if (revenueTotal > 0) {
        const expPercent = formatPercentValue(
          (parseFloat(state.aggregations[0]["-total_current"]) /
            revenueTotal) *
            100,
        )
        const netPercent = formatPercentValue(
          (parseFloat(state.aggregations[1]["-total_current"]) /
            revenueTotal) *
            100,
        )
        state.aggregations[0]["-total_current_percent"] =
          expPercent.toFixed(PERCENT_DECIMALS)
        state.aggregations[1]["-total_current_percent"] =
          netPercent.toFixed(PERCENT_DECIMALS)
      } else {
        state.aggregations[0]["-total_current_percent"] = "0.0"
        state.aggregations[1]["-total_current_percent"] = "0.0"
      }

      const safeOriginalPercent = Number.isFinite(originalPercentNumeric)
        ? originalPercentNumeric
        : 0
      if (original?.line_item !== "REVENUE") {
        // exp
        state.aggregations[0][amountKey] = (
          parseFloat(state.aggregations[0][amountKey]) -
          safeOriginalAmount +
          newAmount
        ).toFixed(AMOUNT_DECIMALS)
        state.aggregations[1][amountKey] = (
          parseFloat(state.aggregations[1][amountKey]) +
          safeOriginalAmount -
          newAmount
        ).toFixed(AMOUNT_DECIMALS)

        // net
        state.aggregations[0][percentKey] = (
          parseFloat(state.aggregations[0][percentKey]) -
          safeOriginalPercent +
          newPercent
        ).toFixed(PERCENT_DECIMALS)
        // state.aggregations[1][percentKey] = (
        //   parseFloat(state.aggregations[1][percentKey]) +
        //   parseFloat(original[percentKey]) -
        //   newPercent
        // ).toFixed(2)
        state.aggregations[1][percentKey] = (
          parseFloat(state.aggregations[1][percentKey]) +
          safeOriginalPercent -
          newPercent
        ).toFixed(PERCENT_DECIMALS)
      } else {
        //exp
        state.aggregations[1][amountKey] = (
          parseFloat(state.aggregations[1][amountKey]) -
          safeOriginalAmount +
          newAmount
        ).toFixed(AMOUNT_DECIMALS)
        // state.aggregations[1][percentKey] = (
        //   parseFloat(state.aggregations[1][percentKey]) +
        //   parseFloat(original[percentKey]) -
        //   newPercent
        // ).toFixed(2)
      }

      // net

      state.budgetData[index] = updatedRow

      //? If revenue changed, recalculate percentages for all rows
      if (original?.line_item === "REVENUE") {
        const newRevenueTotal = getNumericValue(updatedRow["-total_current"]) || 0
        if (newRevenueTotal > 0) {
          state.budgetData.forEach((row: any, rowIndex: number) => {
            if (row.line_item === "REVENUE") {
              state.budgetData[rowIndex]["-total_current_percent"] = 100
            } else {
              const rowTotal = getNumericValue(row["-total_current"]) || 0
              state.budgetData[rowIndex]["-total_current_percent"] =
                formatPercentValue((rowTotal / newRevenueTotal) * 100)
            }
          })
        } else {
          state.budgetData.forEach((row: any, rowIndex: number) => {
            state.budgetData[rowIndex]["-total_current_percent"] = 0
          })
        }
      }
      if (!state.dirtyCells.includes(saveId)) {
        state.dirtyCells.push(saveId)
      }
    },
    resetColumns: (state, _) => {
      state.budgetData = state.rigidBudgetData
      state.aggregations = state.rigidAggregations
      state.dirtyCells = []
    },
    clearDirtyList: (state, _) => {
      state.dirtyCells = []
    },
  },
})

export default pnlSlice.reducer

export const {
  setBudgetData,
  setRigidBudgetData,
  updateCell,
  resetColumns,
  clearDirtyList,
  setYears,
  setSelectedYear,
  setSaved,
} = pnlSlice.actions
